import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Card, TableSearch } from '../../../shared/components'
import DropDown from '../../../shared/components/DropDown'
import Button from '../../../shared/components/Button'
import { OrdersTable } from '../../orders/components/table/OrdersTable'
import { OrderDetailsModal } from '../../orders/components/modal/OrderDetailsModal'
import { useOrdersData } from '../../orders/hooks/useOrders'
import { DropdownProvider } from '../../../shared/utils/DropdownContext'
import { useToast } from '../../../shared/components/toast'
import { handleApiError } from '../../../shared/utils/handleApiError'
import type { Order, OrdersParams } from '../../orders/types/orders.types'
import { STATUS_OPTIONS } from '../../orders/types/filters.types'



// Dashboard widget showing recent orders with search and filters
export const RecentOrdersSection = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('All Status')
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Build API parameters
  const apiParams: OrdersParams = useMemo(() => {
    const params: OrdersParams = {
      'page[size]': 7, // Limit to 7 for dashboard
    }

    // Add search term as item name filter
    if (searchTerm) {
      params['filter[items.itemName]'] = searchTerm
    }

    // Add status filter
    if (statusFilter && statusFilter !== 'All Status') {
      params['filter[status]'] = statusFilter as OrdersParams['filter[status]']
    }

    return params
  }, [searchTerm, statusFilter])

  // Get orders data
  const { orders, isLoading, error, refetch } = useOrdersData(apiParams)

  // Event handlers
  const handleViewOrderDetails = useCallback((order: Order) => {
    setSelectedOrder(order)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedOrder(null)
  }, [])

  const handleViewAllOrders = useCallback(() => {
    navigate('/orders')
  }, [navigate])

  const handleOrderUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    refetch()
  }, [queryClient, refetch])

  // Error handling
  const handleError = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (error && (error as any)?.errorCode !== 1600) {
      const errorMessage = handleApiError(error)
      toast.error('Failed to load recent orders', errorMessage)
    }
  }, [error, toast])

  // Show errors
  useEffect(() => {
    handleError()
  }, [handleError])

  // Filter orders (no status filter needed since it's done on backend)
  const filteredData = useMemo(() => {
    // Input validation: ensure orders is an array
    if (!Array.isArray(orders)) return []
    
    return orders // API already returns filtered and limited data
  }, [orders])

  return (
    <DropdownProvider>
      <Card className="bg-card">
        {/* Header Section */}
        <div className="flex items-center justify-between gap-4 p-6 border-b border-border">
          <h2 className="text-xl font-bold text-foreground">Recent Orders</h2>
          
          <div className="flex items-center gap-4">
            <TableSearch
              onSearch={setSearchTerm}
              placeholder="Search orders..."
              className="w-[250px]"
            />
            
            <DropDown
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-[140px]"
              isActive={statusFilter !== 'All Status'}
            />
            
            <Button
              className="w-[160px] whitespace-nowrap"
              onClick={handleViewAllOrders}
            >
              View all orders
            </Button>
          </div>
        </div>

        {/* Orders Table */}
        <OrdersTable 
          data={filteredData} 
          isLoading={isLoading} 
          onViewDetails={handleViewOrderDetails}
        />

        {/* Order Details Modal */}
        <OrderDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          order={selectedOrder}
          onOrderUpdate={handleOrderUpdate}
        />
      </Card>
    </DropdownProvider>
  )
}